import React, { useMemo } from 'react'
import { GenericTable, NumberedView } from 'components/tables/generic-table'
import styles from './lesson-overview-table.module.css'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AggregatedQuestion, SECTIONS, SECTION_SHORT_HEADERS } from '@all-for-one/shared-types'


export const LessonOverviewTable = ({ questions, difficulty }: { questions: AggregatedQuestion[], difficulty?: string }) => {
  const navigate = useNavigate()
  const { classId, lessonId } = useParams()

  const formatSection = (value?: string) => {
    if (!value) return ''
    const v = value as SECTIONS
    const label = SECTION_SHORT_HEADERS[v]
    return label ? label.replace('%', '').trim() : value
  }

  type TableRow = AggregatedQuestion & { sectionDisplay?: string, rowKey: string }

  const sectionAverages = useMemo(() => {
    const sums: Record<string, { total: number; count: number }> = {}
    for (const q of questions) {
      const key = q.section || ''
      if (!sums[key]) sums[key] = { total: 0, count: 0 }
      sums[key].total += q.overallPercent
      sums[key].count += 1
    }
    const result: Record<string, number> = {}
    for (const [key, { total, count }] of Object.entries(sums)) {
      result[key] = Math.round(total / count)
    }
    return result
  }, [questions])

  const rows: TableRow[] = useMemo(() => {
    const seen = new Set<string>()
    return questions.map(q => {
      const section = (q.section || '').toLowerCase()
      const isFirst = section && !seen.has(section)
      if (section) seen.add(section)
      return {
        ...q,
        sectionDisplay: isFirst ? formatSection(q.section) : '',
        rowKey: `${q.questionId}::${q.section || ''}`
      }
    })
  }, [questions])

  const columns = useMemo(() => ([
    { key: 'section', header: '', className: styles.sectionCol, render: (_v: string, _index: number, row: TableRow) => (
      row.sectionDisplay ? (
        <div className={styles.sectionCell}>
          <span className={styles.sectionName}>{row.sectionDisplay}</span>
          {sectionAverages[row.section || ''] !== undefined && (
            <span className={styles.sectionAvg}>Avg: {sectionAverages[row.section || '']}%</span>
          )}
        </div>
      ) : <span />
    ) },
    { key: 'text', header: 'Questions - (Graded)', className: styles.questionCol, render: (_text: string, index: number, row: AggregatedQuestion) => (
      <Link
        className={styles.lessonLink}
        to={`/classrooms/${classId}/lessons/${lessonId}/questions/${row.questionId}?section=${encodeURIComponent(row.section || '')}&difficulty=${encodeURIComponent(difficulty || '')}`}>
        <div className={styles.nameWrap}>
          {row.displayLabel ? `${row.displayLabel} — ${row.text}` : row.text}
        </div>
      </Link>
    ) },
    { key: 'overallPercent', header: 'Overall%', className: styles.percentCol },
  ]), [rows, sectionAverages])

  const handleDoubleClick = (rowKey: string) => {
    if (!classId || !lessonId) return
    const [qId, sec] = rowKey.split('::')
    navigate(`/classrooms/${classId}/lessons/${lessonId}/questions/${qId}?section=${encodeURIComponent(sec || '')}&difficulty=${encodeURIComponent(difficulty || '')}`)
  }

  return (
    <div className={styles.tableContainer}>
      <GenericTable
        columns={columns}
        data={rows}
        rowKey="rowKey"
        rowClassName={(_, idx) => idx % 2 === 0 ? styles.highlightRow : ''}
        style={{}}
        renderHeader={true}
        renderMoreActions={false}
        doubleClickRowAction={handleDoubleClick}
      />
    </div>
  )
}
